import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit2, Ban, CheckCircle, Trash2, Key, X, Users, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  UserProfile,
  CreateUserInput,
  UpdateUserInput,
  listUsers,
  createUser,
  updateUser,
  suspendUser,
  activateUser,
  deleteUser,
  resetUserPassword
} from '../services/userManagement';

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  const [createForm, setCreateForm] = useState<CreateUserInput>({
    email: '',
    firstName: '',
    lastName: '',
    organizationId: currentUser?.organizationId || '',
    role: 'normal',
    temporaryPassword: ''
  });

  const [editForm, setEditForm] = useState<UpdateUserInput>({});

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await listUsers({
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        search: searchTerm || undefined
      });
      setUsers(result.users);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, statusFilter, searchTerm]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreateUser = async () => {
    try {
      const result = await createUser(createForm);
      alert(`User created successfully!\nTemporary password: ${result.temporaryPassword}`);
      setShowCreateModal(false);
      setCreateForm({ email: '', firstName: '', lastName: '', organizationId: currentUser?.organizationId || '', role: 'normal', temporaryPassword: '' });
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to create user: ${err.message}`);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      await updateUser(selectedUser.userId, editForm);
      alert('User updated successfully!');
      setShowEditModal(false);
      setSelectedUser(null);
      setEditForm({});
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to update user: ${err.message}`);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    const reason = window.prompt('Reason for suspension (optional):');
    if (reason === null) return;
    try {
      await suspendUser(userId, reason || undefined);
      alert('User suspended successfully!');
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to suspend user: ${err.message}`);
    }
  };

  const handleActivateUser = async (userId: string) => {
    if (!window.confirm('Activate this user?')) return;
    try {
      await activateUser(userId);
      alert('User activated successfully!');
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to activate user: ${err.message}`);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await deleteUser(userId);
      alert('User deleted successfully!');
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to delete user: ${err.message}`);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!window.confirm('Reset password for this user?')) return;
    try {
      const result = await resetUserPassword(userId);
      alert(`Password reset successfully! Temporary password: ${result.temporaryPassword}`);
    } catch (err: any) {
      alert(`Failed to reset password: ${err.message}`);
    }
  };

  const openEditModal = (user: UserProfile) => {
    setSelectedUser(user);
    setEditForm({ firstName: user.firstName, lastName: user.lastName, organizationId: user.organizationId, role: user.role, enabledFeatures: user.enabledFeatures || [] });
    setShowEditModal(true);
  };

  const AVAILABLE_FEATURES = [
    { id: 'vexa', label: 'Teams Integration (Vexa)' },
    { id: 'miro', label: 'Miro Integration' },
    { id: 'sandbox', label: 'Sandbox Environment' },
    { id: 'claude_code_worker', label: 'Claude Code Worker' },
    { id: 'rooms', label: 'Room Scoping' },
    { id: 'open_design', label: 'Open Design (AI UI Generation)' },
    { id: 'workshop_book', label: 'Workshop Book (Printed A5 Takeaway)' },
    { id: 'reach', label: 'REACH (Change Management)' },
  ];

  const toggleFeature = (featureId: string) => {
    const current = editForm.enabledFeatures || [];
    const updated = current.includes(featureId) ? current.filter(f => f !== featureId) : [...current, featureId];
    setEditForm({ ...editForm, enabledFeatures: updated });
  };

  function getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'system_admin': return 'bg-red-100 text-red-700 border border-red-200';
      case 'avatar_admin': return 'bg-purple-100 text-purple-700 border border-purple-200';
      case 'normal': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'read_only': return 'bg-slate-100 text-slate-500 border border-slate-200';
      default: return 'bg-slate-100 text-slate-500 border border-slate-200';
    }
  }

  function getRoleDisplayName(role: string): string {
    switch (role) {
      case 'system_admin': return 'System Admin';
      case 'avatar_admin': return 'Avatar Admin';
      case 'normal': return 'User';
      case 'read_only': return 'Read Only';
      default: return role;
    }
  }

  function getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'suspended': return 'bg-amber-100 text-amber-700 border border-amber-200';
      case 'deleted': return 'bg-red-100 text-red-600 border border-red-200';
      default: return 'bg-slate-100 text-slate-500 border border-slate-200';
    }
  }

  const filteredUsers = users.filter(user =>
    searchTerm === '' ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const inputCls = "w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none";
  const selectCls = "w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white";

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Users size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">User Management</h1>
              <p className="text-slate-500 text-sm font-medium">Manage user accounts, roles, and permissions</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Plus size={16} /> Create User
          </button>
        </header>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <AlertCircle size={16} className="shrink-0" /> {error}
          </div>
        )}

        {/* Filter + Table */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
                  className="w-full pl-8 pr-3 py-2 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                <option value="">All Roles</option>
                <option value="system_admin">System Admin</option>
                <option value="avatar_admin">Avatar Admin</option>
                <option value="normal">User</option>
                <option value="read_only">Read Only</option>
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
              <span className="ml-auto text-xs text-slate-400">{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Users size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">User</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Organisation</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Role</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Features</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Last Login</th>
                    <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {filteredUsers.map(user => (
                    <tr key={user.userId} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3 max-w-[200px]">
                        <p className="font-medium text-navy truncate" title={user.displayName}>{user.displayName}</p>
                        <p className="text-xs text-slate-400 truncate mt-0.5" title={user.email}>{user.email}</p>
                      </td>
                      <td className="px-5 py-3 text-slate-500">{user.organizationId}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${getRoleBadgeClass(user.role)}`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusBadgeClass(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex gap-1.5 flex-wrap">
                          {(user.enabledFeatures || []).map(f => (
                            <span key={f} className="inline-block px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold">
                              {f}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-slate-400 text-xs">
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => openEditModal(user)} title="Edit"
                            className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => handleResetPassword(user.userId)} title="Reset Password"
                            className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors">
                            <Key size={13} />
                          </button>
                          {user.status === 'active' ? (
                            <button onClick={() => handleSuspendUser(user.userId)} title="Suspend"
                              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors">
                              <Ban size={13} />
                            </button>
                          ) : (
                            <button onClick={() => handleActivateUser(user.userId)} title="Activate"
                              className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors">
                              <CheckCircle size={13} />
                            </button>
                          )}
                          {user.userId !== currentUser?.userId && (
                            <button onClick={() => handleDeleteUser(user.userId)} title="Delete"
                              className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-navy">Create New User</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Email *</label>
                <input type="email" value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">First Name *</label>
                  <input type="text" value={createForm.firstName}
                    onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                    className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Last Name *</label>
                  <input type="text" value={createForm.lastName}
                    onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                    className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Organisation ID *</label>
                <input type="text" value={createForm.organizationId}
                  onChange={(e) => setCreateForm({ ...createForm, organizationId: e.target.value })}
                  className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Role *</label>
                <select value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as any })}
                  className={selectCls}>
                  <option value="normal">User</option>
                  <option value="read_only">Read Only</option>
                  <option value="avatar_admin">Avatar Admin</option>
                  <option value="system_admin">System Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Temporary Password</label>
                <input type="password" value={createForm.temporaryPassword}
                  onChange={(e) => setCreateForm({ ...createForm, temporaryPassword: e.target.value })}
                  placeholder="Leave blank to auto-generate"
                  className={inputCls} />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleCreateUser}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
                  Create User
                </button>
                <button onClick={() => setShowCreateModal(false)}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-navy">Edit User</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">First Name</label>
                  <input type="text" value={editForm.firstName || ''}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Last Name</label>
                  <input type="text" value={editForm.lastName || ''}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Organisation ID</label>
                <input type="text" value={editForm.organizationId || ''}
                  onChange={(e) => setEditForm({ ...editForm, organizationId: e.target.value })}
                  className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Role</label>
                <select value={editForm.role || selectedUser.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value as any })}
                  className={selectCls}>
                  <option value="normal">User</option>
                  <option value="read_only">Read Only</option>
                  <option value="avatar_admin">Avatar Admin</option>
                  <option value="system_admin">System Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Feature Flags</label>
                <div className="flex flex-col gap-1 p-3 bg-slate-50/60 border border-[#e2e8f0] rounded-xl">
                  {AVAILABLE_FEATURES.map(feature => {
                    const enabled = (editForm.enabledFeatures || []).includes(feature.id);
                    return (
                      <label key={feature.id}
                        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${enabled ? 'bg-primary/5' : 'hover:bg-slate-100'}`}>
                        <input type="checkbox" checked={enabled} onChange={() => toggleFeature(feature.id)}
                          className="w-4 h-4 accent-primary rounded" />
                        <span className={`text-sm ${enabled ? 'font-semibold text-navy' : 'text-slate-500'}`}>
                          {feature.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleUpdateUser}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
                  Update User
                </button>
                <button onClick={() => setShowEditModal(false)}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
